// One-time onboarding card shown to new reseller accounts on their
// first authenticated visit. Floats above the bottom-right corner so
// it doesn't fight the layout, dismissible by clicking the X — dismissal
// is persisted in localStorage so the hint shows only once per browser
// + account ever.
//
// Versioned key so we can ship a refreshed hint in the future without
// re-triggering it for existing accounts. v2 marks the BuyerGram-reseller
// rewrite — v1 was the old CloudGram "press 'I want this' to chat with a
// manager" copy, which doesn't describe what resellers actually do here.
//
// Only shown when user.role === "customer". Staff get straight to work.
//
// The "Завершить настройку" button calls onOpenForwardSettings, which
// the parent (App.jsx) wires up to open SettingsModal with
// initialTab="forward" — this saves the user from hunting for the right
// tab themselves.
import { useState, useEffect } from "react";
import { T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { useT } from "../i18n.jsx";

const SEEN_KEY = "cg_seen_intro_v2";

export function FirstRunHint({ user, onOpenForwardSettings }) {
  const t = useT();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "customer") return;
    try {
      if (!localStorage.getItem(SEEN_KEY)) {
        // Wait a beat so the hint doesn't flash up at the same moment as
        // the app shell hydrates.
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage disabled — just don't show */ }
  }, [user]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
  };

  const goToSettings = () => {
    dismiss();
    onOpenForwardSettings?.();
  };

  return (
    <div
      role="dialog" aria-labelledby="cg-intro-title"
      style={{
        position: "fixed",
        bottom: 18, right: 18,
        width: 380, maxWidth: "92vw",
        background: T_NEW.paperRaised, color: T_NEW.ink,
        borderRadius: T_NEW.r2, padding: 20,
        boxShadow: T_NEW.shadowModal,
        border: `1px solid ${T_NEW.ink12}`,
        animation: "fadeUp 0.25s ease-out",
        zIndex: 180,
        fontFamily: T_NEW.fontSans,
      }}>
      {/* Editorial "from the editors" header — small persimmon stamp dot
          + mono label, then a serif headline below. Reads as a hand-set
          welcome card rather than a tooltip. */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ display: "inline-block", width: 8, height: 8,
                           borderRadius: "50%", background: T_NEW.persimmon }} />
            <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                           color: T_NEW.ink56, textTransform: "uppercase",
                           letterSpacing: T_NEW.monoTrack, fontWeight: 700 }}>
              {t("intro.kicker")}
            </span>
          </div>
          <div id="cg-intro-title" style={{ fontFamily: T_NEW.fontSerif,
                                              fontSize: T_NEW.displayMd,
                                              fontWeight: 500, lineHeight: 1.2,
                                              color: T_NEW.ink }}>
            {t("intro.title")}
          </div>
        </div>
        <button onClick={dismiss}
                aria-label={t("balance.cancel")}
                style={{ background: "none", border: "none", cursor: "pointer",
                         padding: 2, display: "flex", marginLeft: 10,
                         color: T_NEW.ink56 }}>
          {I.close()}
        </button>
      </div>
      <p style={{ margin: "0 0 12px 0", color: T_NEW.ink56,
                  fontSize: T_NEW.meta, lineHeight: 1.5 }}>
        {t("intro.lead")}
      </p>
      <ol style={{ margin: 0, padding: 0, listStyle: "none",
                   fontSize: T_NEW.meta, lineHeight: 1.55,
                   color: T_NEW.ink, fontFamily: T_NEW.fontSans, counterReset: "step" }}>
        {[t("intro.step1"), t("intro.step2"), t("intro.step3")].map((step, i) => (
          <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start",
                                marginBottom: i < 2 ? 10 : 0 }}>
            <span style={{ color: T_NEW.persimmon, fontWeight: 700,
                           fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
                           minWidth: 18, textAlign: "right", flexShrink: 0 }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {/* Two-action footer: primary CTA jumps directly to Settings →
          Forward so the reseller can configure FX + channel right now;
          secondary dismisses to "I'll do it later". */}
      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <button
          onClick={goToSettings}
          style={{
            flex: 1,
            background: T_NEW.persimmon, color: T_NEW.paperOnInk,
            border: "none", borderRadius: T_NEW.r1,
            padding: "11px 16px", fontSize: T_NEW.micro, fontWeight: 700,
            cursor: "pointer", fontFamily: T_NEW.fontMono,
            textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
          }}>
          {t("intro.cta")}
        </button>
        <button
          onClick={dismiss}
          style={{
            background: "transparent", color: T_NEW.ink56,
            border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
            padding: "11px 14px", fontSize: T_NEW.micro, fontWeight: 700,
            cursor: "pointer", fontFamily: T_NEW.fontMono,
            textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
          }}>
          {t("intro.later")}
        </button>
      </div>
    </div>
  );
}
